import React, { useEffect, useRef, useState } from 'react';
import { loadMenuPages, peekMenuPages } from '../services/menu-pages';
import './MenuPage2.css';

function BookPage({ page, onLoad }) {
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  if (!page) return <div className="cafe-book__page" />;
  return <div className={`cafe-book__page${page.cover ? ' cafe-book__page--cover' : ''}`}>
    <img src={page.image_url + (retry ? `?retry=${retry}` : '')} alt={page.title} draggable="false"
      onLoad={() => { setFailed(false); onLoad?.(); }} onError={() => { setFailed(true); onLoad?.(); }} />
    {failed && <button className="cafe-book__retry" onClick={() => setRetry(Date.now())}>Sayfayı tekrar yükle</button>}
  </div>;
}
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export default function MenuPage2({ onReady }) {
  const [pages, setPages] = useState(() => peekMenuPages() || []);
  const [index, setIndex] = useState(0);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [flip, setFlip] = useState(null);
  const flipRef = useRef(null);
  const [layout, setLayout] = useState(null);
  const viewportRef = useRef(null);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const timer = useRef(null);
  const callbackRef = useRef(onReady); callbackRef.current = onReady;
  const cover = { image_url: '/images/menu-kapak2.webp', title: 'Kukas Cafe menü kapağı', cover: true };
  const sheet = i => i === 0 ? cover : pages[i - 1];
  const spread = layout && !layout.portrait && index > 0;
  const width = layout?.width || 300;
  const height = width * 1.5;
  const bookWidth = width * (spread ? 2 : 1);

  const updateView = next => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const maxX = Math.max(0, (bookWidth * next.zoom - (rect?.width || bookWidth)) / 2 + 16);
    const maxY = Math.max(0, (height * next.zoom - (rect?.height || height)) / 2 + 16);
    const bounded = { ...next, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
    viewRef.current = bounded; setView(bounded);
  };
  const zoomTo = zoom => updateView({ zoom: clamp(zoom, 0.75, 3), x: 0, y: 0 });

  useEffect(() => {
    let active = true;
    loadMenuPages(attempt > 0).then(data => { if (active) { setPages(data); setError(false); } })
      .catch(() => { if (active) { setError(true); callbackRef.current?.(); } });
    return () => { active = false; };
  }, [attempt]);
  useEffect(() => {
    pages.forEach(page => { const image = new Image(); image.src = page.image_url; });
  }, [pages]);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const { width: available, height: availableHeight } = entry.contentRect;
      const portrait = available < 700;
      const pageWidth = Math.max(80, Math.floor(Math.min((available - 32) / (portrait ? 1 : 2), (availableHeight - 28) / 1.5, 480)));
      setLayout({ width: pageWidth, portrait });
      clearTimeout(timer.current); flipRef.current = null; setFlip(null);
      setIndex(value => portrait || value === 0 ? value : value % 2 ? value : value - 1);
      viewRef.current = { zoom: 1, x: 0, y: 0 }; setView(viewRef.current);
      pointers.current.clear(); gesture.current = null;
    });
    observer.observe(viewportRef.current);
    return () => { observer.disconnect(); clearTimeout(timer.current); };
  }, []);

  const lastIndex = layout?.portrait ? pages.length : Math.max(1, pages.length % 2 ? pages.length : pages.length - 1);
  const turn = direction => {
    if (!pages.length || !layout) return;
    const current = flipRef.current?.next ?? index;
    const next = direction > 0 ? (current === 0 ? 1 : current + (layout.portrait ? 1 : 2)) : (current <= 1 ? 0 : current - (layout.portrait ? 1 : 2));
    if (next < 0 || next > lastIndex || next === current) return;
    clearTimeout(timer.current);
    setIndex(current);
    const transition = { next, direction };
    flipRef.current = transition; setFlip(transition); zoomTo(1);
    timer.current = setTimeout(() => {
      setIndex(next); setFlip(null); flipRef.current = null;
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 780);
  };

  const beginGesture = () => {
    const points = [...pointers.current.values()];
    const rect = viewportRef.current.getBoundingClientRect();
    const center = points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0];
    gesture.current = center ? { ...viewRef.current, center, distance: points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0, origin: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } } : null;
  };
  const pointerDown = event => {
    if (event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY }); beginGesture();
  };
  const pointerMove = event => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()]; const start = gesture.current;
    if (points.length > 1) {
      const center = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      const zoom = clamp(start.zoom * Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) / Math.max(1, start.distance), 0.75, 3);
      const ratio = zoom / start.zoom;
      updateView({ zoom, x: center.x - start.origin.x - (start.center.x - start.origin.x - start.x) * ratio, y: center.y - start.origin.y - (start.center.y - start.origin.y - start.y) * ratio });
    } else if (viewRef.current.zoom > 1) {
      updateView({ zoom: start.zoom, x: start.x + points[0].x - start.center.x, y: start.y + points[0].y - start.center.y });
    }
  };
  const pointerEnd = event => { pointers.current.delete(event.pointerId); beginGesture(); };
  const front = flip && (flip.direction > 0 ? sheet(spread ? index + 1 : index) : sheet(index));
  const back = flip && sheet(flip.direction > 0 ? flip.next : !layout?.portrait && flip.next > 0 ? flip.next + 1 : flip.next);

  return <section className="cafe-book" aria-label="Kukas Cafe menü kitabı" onKeyDown={event => {
    if (event.key === 'ArrowRight') { event.preventDefault(); turn(1); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); turn(-1); }
  }}>
    <header className="cafe-book__header"><img src="/images/logo3.webp" alt="Kukas Cafe" /></header>
    <div className="cafe-book__zoom" aria-label="Yakınlaştırma kontrolleri">
      <button aria-label="Yakınlaştır" onClick={() => zoomTo(view.zoom + 0.25)} disabled={view.zoom >= 3 || !!flip}>+</button>
      <button aria-label="Yakınlaştırmayı sıfırla" onClick={() => zoomTo(1)}>{Math.round(view.zoom * 100)}%</button>
      <button aria-label="Uzaklaştır" onClick={() => zoomTo(view.zoom - 0.25)} disabled={view.zoom <= 0.75 || !!flip}>−</button>
    </div>
    <div className="cafe-book__viewport" ref={viewportRef} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerEnd} onPointerCancel={pointerEnd} onLostPointerCapture={pointerEnd}>
      {error ? <div className="cafe-book__message" role="alert">Menü yüklenemedi. <button onClick={() => setAttempt(value => value + 1)}>Tekrar dene</button></div> : layout && pages.length ?
        <div className="cafe-book__scale" style={{ width: bookWidth, height, transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})` }}>
          <div className="cafe-book__binding" data-page={index} style={{ width: bookWidth, height }}>
            <div className="cafe-book__stationary">
              <BookPage page={sheet(flip && (!spread || flip.direction < 0) ? flip.next : index)} onLoad={() => callbackRef.current?.()} />
              {spread && <BookPage page={sheet(flip && flip.direction > 0 ? flip.next + 1 : index + 1)} />}
            </div>
            {spread && <div className="cafe-book__spine" />}
            {flip && <div key={`${index}-${flip.next}`} aria-hidden="true" className={`cafe-book__leaf cafe-book__leaf--${flip.direction > 0 ? 'forward' : 'backward'}${spread ? ' cafe-book__leaf--spread' : ''}`} style={{ width }}>
              <div className="cafe-book__face"><BookPage page={front} /></div>
              <div className="cafe-book__face cafe-book__face--back"><BookPage page={back} /></div>
            </div>}
          </div>
        </div> : <div className="cafe-book__message" role="status">Menü yükleniyor…</div>}
    </div>
    <nav className="cafe-book__navigation" aria-label="Kitap sayfaları">
      <button aria-label="Önceki sayfa" onClick={() => turn(-1)} disabled={!pages.length || (flip?.next ?? index) === 0}>←</button>
      <span aria-live="polite">{index === 0 ? '' : `${index}${spread && index < pages.length ? `–${index + 1}` : ''} / ${pages.length}`}</span>
      <button aria-label="Sonraki sayfa" onClick={() => turn(1)} disabled={!pages.length || (flip?.next ?? index) >= lastIndex}>→</button>
    </nav>
  </section>;
}
